import { useState, useEffect, useRef } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LogOut, Shield, Map as MapIcon, Coins, Dice5, Search, Home as HomeIcon, Link2, ShoppingBag, Swords, Skull, Castle, House, Sparkles, Zap, Sliders, Gift, LifeBuoy, Gauge, GitBranch, Send, Hammer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Currency } from "@/components/Atoms";
import { ChilletSkull } from "@/components/ChilletSkull";
import { OnlinePlayersPill } from "@/components/OnlinePlayersPill";
import { LiveGiveawaysPill } from "@/components/LiveGiveawaysPill";
import ChatWidget from "@/components/chat/ChatWidget";
import { PayoutFlash } from "@/components/PayoutFlash";
import { RaidBanner } from "@/components/RaidBanner";

// Nav split into themed sections so the menu is scannable on every
// viewport — mobile/desktop now share the same grouping and the
// section headers double as visual landmarks while you scroll. The
// flat list previously crammed all 17 destinations into a single
// alphabetical column on phones, which the user flagged as hard to
// navigate.
const NAV_SECTIONS = [
  {
    title: "Account",
    items: [
      { to: "/",           label: "Home",       icon: House },
      { to: "/dashboard",  label: "Dashboard",  icon: HomeIcon },
      { to: "/linking",    label: "Link",       icon: Link2 },
      { to: "/support",    label: "Support",    icon: LifeBuoy },
    ],
  },
  {
    title: "Gameplay",
    items: [
      { to: "/shop",       label: "Shop",       icon: ShoppingBag },
      { to: "/palgame",    label: "Pal Game",   icon: Swords },
      { to: "/breeding",   label: "Breeding",   icon: Sparkles },
      { to: "/skills",     label: "Skills",     icon: Zap },
      { to: "/builder",    label: "Pal Builder", icon: Sliders },
      { to: "/gambling",   label: "Gambling",   icon: Dice5 },
    ],
  },
  {
    title: "Tools",
    items: [
      { to: "/planner",    label: "Blueprint",  icon: Hammer },
      { to: "/giveaways",  label: "Giveaways",  icon: Gift },
      { to: "/map",        label: "Map",        icon: MapIcon },
      { to: "/raids",      label: "Raid Hunt",  icon: Swords },
      { to: "/pvp",        label: "PVP Board",  icon: Skull },
    ],
  },
  {
    title: "Reference",
    items: [
      { to: "/lookup",     label: "Paldex",     icon: Search },
      { to: "/tech-tree",  label: "Game Codex", icon: GitBranch },
      { to: "/towers",     label: "Towers",     icon: Castle },
      { to: "/server-settings", label: "Server Settings / Mods", icon: Gauge },
    ],
  },
];
// Flat view kept for any future code that needs to iterate every
// destination without caring about the grouping.
const NAV = NAV_SECTIONS.flatMap((s) => s.items);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  // Live-filter for the nav sheet — typing in the search box at the
  // top hides any nav entry whose label doesn't contain the query.
  // Sections with zero matches collapse entirely so the user never
  // sees an orphan section header. Reset to "" whenever the menu
  // closes so reopening starts fresh.
  const [navQuery, setNavQuery] = useState("");
  useEffect(() => { if (!open) setNavQuery(""); }, [open]);
  // Skull-burst on incoming transfers — fires globally so the
  // cinematic punches through any page (Dashboard, Shop, Map, etc.).
  const [transferFlash, setTransferFlash] = useState(null);
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Body-scroll lock when the mobile menu sheet is open — the menu
  // now covers the full viewport below the header on phones, so
  // scrolling the page behind it would feel buggy. We only lock on
  // narrow viewports; desktop's small dropdown doesn't need it.
  useEffect(() => {
    if (!open) return;
    const isMobile = window.matchMedia("(max-width: 639px)").matches;
    if (!isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Four pages get the commemorative Chillet coin photo as their
  // page-wide backdrop instead of the global Palworld fan-art — the
  // four "currency-centric" surfaces of the site. Everywhere else
  // (Dashboard, Map, Towers, Paldex, Pal-detail, Admin, Auth, etc.)
  // stays on the fan-art.
  const COIN_BG_ROUTES = ["/economy", "/shop", "/palgame", "/gambling"];
  const useCoinBg = COIN_BG_ROUTES.some((p) =>
    loc.pathname === p || loc.pathname.startsWith(p + "/")
  );
  // Per-currency-page tint — each gameplay surface gets its own subtle
  // colour overlay on top of the shared coin backdrop so the pages
  // still feel distinct visually. Maps to a `data-coin-tint` attr
  // consumed by `.global-bg--coin[data-coin-tint=...]` CSS rules.
  const coinTint =
    loc.pathname.startsWith("/economy")  ? "teal"   :
    loc.pathname.startsWith("/shop")     ? "amber"  :
    loc.pathname.startsWith("/palgame")  ? "purple" :
    loc.pathname.startsWith("/gambling") ? "red"    : null;

  // Parallax tilt on the coin backdrop — follows the cursor with a soft
  // ±8° rotateX/Y so the coin feels alive without distracting from the
  // UI. Uses requestAnimationFrame throttling + a CSS custom property
  // on the coin image so React never re-renders during pointer moves.
  const coinImgRef = useRef(null);
  useEffect(() => {
    if (!useCoinBg) return;
    let rafId = 0;
    let nextX = 0, nextY = 0;
    const onMove = (e) => {
      // Range −1..1 across the viewport.
      const nx = (e.clientX / window.innerWidth) * 2 - 1;
      const ny = (e.clientY / window.innerHeight) * 2 - 1;
      nextX = -ny * 8; // tilt away from cursor
      nextY = nx * 8;
      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          if (coinImgRef.current) {
            coinImgRef.current.style.setProperty("--coin-tilt-x", `${nextX.toFixed(2)}deg`);
            coinImgRef.current.style.setProperty("--coin-tilt-y", `${nextY.toFixed(2)}deg`);
          }
        });
      }
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [useCoinBg]);

  // Listen for real-time "transfer received" events pushed over the
  // economy WebSocket (see AuthContext.jsx) and surface them as a toast
  // *plus* the floating Chillet-skull burst, so the recipient gets a
  // proper cinematic for incoming skulls no matter what page they're on.
  useEffect(() => {
    const onTransfer = (e) => {
      const { from_name, amount } = e.detail || {};
      if (!amount) return;
      toast.success(`🪙 +${amount.toLocaleString()} from ${from_name}`);
      setTransferFlash({ amount, from: from_name || "Someone" });
    };
    window.addEventListener("chillets:transfer-received", onTransfer);
    return () => window.removeEventListener("chillets:transfer-received", onTransfer);
  }, [toast]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
      {/* Global painterly Palworld backdrop on most routes; Economy /
          Shop / Pal Game swap in the commemorative Chillet coin as the
          pinned backdrop so those gameplay surfaces "feel like" the
          currency they revolve around. Heavily dimmed so the foreground
          UI stays readable. `position: fixed` keeps it pinned to the
          viewport so it doesn't scroll with content. */}
      <div
        className={`global-bg ${useCoinBg ? "global-bg--coin" : ""}`}
        data-coin-tint={coinTint || undefined}
        aria-hidden="true"
      >
        {useCoinBg ? (
          <img ref={coinImgRef} src="/assets/auth/chillet_coin_bg.jpg" alt="" className="global-bg__art global-bg__art--coin" />
        ) : (
          <img src="/assets/auth/palworld_fanart.jpg" alt="" className="global-bg__art" />
        )}
        <div className="global-bg__veil" />
        {/* Floating Chillet coin decorations — purely cosmetic, hidden
            on mobile so they don't crowd the content. Skip these on the
            coin-background routes; the giant centered coin is enough. */}
        {!useCoinBg && (
          <>
            <img src="/icons/chillet_coin_heads.png" alt="" className="global-bg__coin global-bg__coin--tl" />
            <img src="/icons/chillet_coin_tails.png" alt="" className="global-bg__coin global-bg__coin--br" />
          </>
        )}
      </div>
    <div className="skull-cursor min-h-screen flex flex-col" style={{ position: "relative", zIndex: 2 }}>
      {/* Top nav */}
      <header className="sticky top-0 z-50 backdrop-blur-md border-b border-[var(--teal-soft)] bg-[rgba(6,3,10,0.85)]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 h-14">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-logo">
            <ChilletSkull size={26} className="text-[var(--teal)]" style={{ filter: "drop-shadow(0 0 8px var(--teal-glow))" }} />
            <span className="text-base sm:text-xl glow-teal whitespace-nowrap" style={{ fontFamily: "'Cinzel Decorative', serif", letterSpacing: "0.06em" }}>
              CHILLPALSERVERS
            </span>
          </Link>

          {/* Live player count + giveaway pill. NOTE: this used to read
              `hidden xs:flex sm:flex`, but no `xs` breakpoint is defined in
              tailwind.config.js, so `xs:flex` compiled to nothing and the
              intended phone-portrait behaviour never existed. Kept hidden
              below `sm` deliberately — the brand mark, both pills and the
              menu button don't fit in a 56px bar at 390px. */}
          <div className="hidden sm:flex items-center gap-2">
            <OnlinePlayersPill />
            <LiveGiveawaysPill />
          </div>

          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden sm:flex items-center gap-3">
                <Currency value={user.points} />
                <span className="text-sm text-[var(--muted)] truncate max-w-[120px]" data-testid="user-name">
                  {user.username}
                </span>
                {user.is_admin && (
                  <Link
                    to="/admin"
                    className="badge text-[var(--warning)] border-[var(--warning)] hover:bg-[rgba(255,200,80,0.1)]"
                    data-testid="admin-badge"
                    title={
                      user.admin_source === "discord"
                        ? "Admin via Discord role — auto-synced on every login"
                        : user.admin_source === "env"
                        ? "Admin via .env master credentials"
                        : user.admin_source === "promotion"
                        ? "Admin via promotion by another admin"
                        : "Admin Panel"
                    }
                  >
                    <Shield size={10} className="inline mr-1" />
                    Admin{user.admin_source ? ` · ${
                      user.admin_source === "discord" ? "Discord"
                        : user.admin_source === "env" ? "Master"
                        : "Promoted"
                    }` : ""}
                  </Link>
                )}
                {/* Logout intentionally lives only inside the Menu
                    dropdown now — having it here too was redundant. */}
              </div>
            )}
            <button
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 text-[var(--bone)] hover:text-[var(--teal)] hover:border-[var(--teal-soft)] hover:bg-[rgba(0,229,255,0.06)] transition-colors"
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-menu-toggle"
              aria-label="Toggle navigation menu"
              aria-expanded={open}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
              <span className="hidden sm:inline text-xs uppercase tracking-[0.18em]">Menu</span>
            </button>
          </div>
        </div>

        {/* Unified nav menu — same panel on every viewport, but with
            two distinct layouts driven by tailwind breakpoints:
              • Mobile (`< sm`): full-height sheet that drops below the
                header and covers the viewport. Body scroll is locked
                (see effect above) so users can flick through the menu
                without the page scrolling underneath. Two-column grid
                so all 17 destinations fit in roughly one screen height
                instead of forcing a long scroll.
              • Desktop (`≥ sm`): compact dropdown that hangs below
                the header, same look as before — 2 to 4 columns
                depending on width. Section headers now group entries.
            User flagged the old mobile menu as hard to scan because
            17 items in a single alphabetical column blew past the
            viewport. The sectioned layout + two-column grid + sticky
            user pill addresses that without touching desktop muscle
            memory. */}
        {open && (
          <div
            // iOS Safari URL-bar fix: use `dvh` (dynamic viewport
            // height) instead of `bottom-0` so the sheet always
            // measures to the *visible* viewport, never hidden
            // behind the auto-collapsing address bar. User reported
            // on phone-portrait the bottom of the menu was unreachable
            // and they had to rotate to landscape to see everything.
            className="border-t border-[var(--teal-soft)] bg-[rgba(6,3,10,0.97)] backdrop-blur-md fixed sm:absolute inset-x-0 top-14 h-[calc(100dvh-3.5rem)] sm:h-auto sm:max-h-[80vh] overflow-y-auto animate-slide-down-soft"
            data-testid="nav-dropdown"
          >
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 pb-12 sm:pb-6">
              {/* Sticky user pill — only on mobile (the desktop header
                  already shows currency + username + admin badge to
                  the left of the menu button). Keeps the context
                  visible as the user scrolls through the four sections. */}
              {user && (
                <div className="sticky top-0 z-10 -mx-4 sm:hidden bg-[rgba(6,3,10,0.97)] backdrop-blur-md flex items-center justify-between py-3 px-4 mb-3 border-b border-white/10">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium truncate">{user.username}</span>
                    {user.is_admin && (
                      <span className="badge text-[var(--warning)] border-[var(--warning)] shrink-0" title="Admin">
                        <Shield size={10} className="inline mr-0.5" />
                        Admin
                      </span>
                    )}
                  </div>
                  <Currency value={user.points} />
                </div>
              )}

              {/* Live filter — typing here hides any nav entry whose
                  label doesn't contain the query, and collapses any
                  section header with zero matches. Solves the "17
                  items is a lot to scroll through" problem without
                  forcing users to rely on scanning. iOS doesn't
                  auto-focus on programmatic focus inside touchstart
                  flows reliably, so we don't autoFocus — user taps
                  the field when they want to type. */}
              <div className="relative mb-3" data-testid="nav-search-wrap">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
                <input
                  type="search"
                  value={navQuery}
                  onChange={(e) => setNavQuery(e.target.value)}
                  placeholder="Find a page…"
                  data-testid="nav-search-input"
                  className="w-full h-11 sm:h-9 pl-9 pr-3 rounded-md bg-[rgba(255,255,255,0.04)] border border-white/10 text-sm text-[var(--bone)] placeholder:text-[var(--dim)] focus:outline-none focus:border-[var(--teal-soft)] focus:bg-[rgba(0,229,255,0.04)] transition-colors"
                />
              </div>

              {/* Sections — phone-portrait gets 1 col with full-width
                  tiles (no label truncation — "Server Settings / Mods"
                  fits on one line). Tablets and up keep the 2/3-col
                  grid as before. Search bar above mitigates the
                  "long scroll" problem the user originally reported. */}
              {NAV_SECTIONS.map((section) => {
                const q = navQuery.trim().toLowerCase();
                const matches = q
                  ? section.items.filter((it) => it.label.toLowerCase().includes(q))
                  : section.items;
                if (matches.length === 0) return null;
                return (
                  <div key={section.title} className="mb-4 last:mb-0">
                    {/* Hide section headers while filtering — the
                        grouping is noise when the user is hunting for
                        a specific destination. */}
                    {!q && (
                      <div
                        className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted)] px-2 mb-1.5"
                        data-testid={`nav-section-${section.title.toLowerCase()}`}
                      >
                        {section.title}
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                      {matches.map((it) => {
                      // NAV entries with a `?tab=` deep-link need a
                      // search-aware active check — without it, opening
                      // "Work" (`/palgame?tab=economy`) would highlight
                      // both Work and Pal Game because NavLink's default
                      // isActive ignores the query string.
                      const [navPath, navSearch] = it.to.split("?");
                      const expectedTab = new URLSearchParams(navSearch || "").get("tab");
                      return (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          end={it.to === "/"}
                          data-testid={`nav-${it.label.toLowerCase().replace(/[ /]/g, "-")}-link`}
                          className={({ isActive }) => {
                            const onSamePath = loc.pathname === navPath;
                            const currentTab = new URLSearchParams(loc.search).get("tab");
                            const tabMatch = expectedTab
                              ? currentTab === expectedTab
                              : !currentTab || !NAV.some((n) =>
                                  n.to.startsWith(navPath + "?tab=" + currentTab));
                            const reallyActive = isActive && onSamePath && tabMatch;
                            // Larger min-height (h-11 ≈ 44px) on mobile
                            // for fat-finger tap targets; tighter on
                            // desktop (sm:py-2.5).
                            return `flex items-center gap-3 px-3 h-11 sm:h-auto sm:py-2.5 rounded-md transition-colors ${
                              reallyActive
                                ? "text-[var(--teal)] bg-[rgba(0,229,255,0.08)]"
                                : "text-[var(--bone)] hover:text-[var(--teal)] hover:bg-[rgba(0,229,255,0.04)]"
                            }`;
                          }}
                        >
                          <it.icon size={16} className="shrink-0" />
                          <span className="text-sm truncate">{it.label}</span>
                        </NavLink>
                      );
                    })}
                    </div>
                  </div>
                );
              })}

              {/* Empty state — only fires when the search query
                  filtered every section into oblivion. Gives the user
                  a clear "no matches" cue instead of a blank menu. */}
              {navQuery.trim() && NAV_SECTIONS.every(
                (s) => s.items.filter(
                  (it) => it.label.toLowerCase().includes(navQuery.trim().toLowerCase())
                ).length === 0
              ) && (
                <div
                  className="text-sm text-[var(--muted)] text-center py-6"
                  data-testid="nav-search-empty"
                >
                  No pages match <span className="text-[var(--bone)]">&ldquo;{navQuery}&rdquo;</span>.
                </div>
              )}

              {(user?.is_admin || user) && (
                <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap gap-2">
                  {user?.is_admin && (
                    <NavLink to="/admin" data-testid="nav-admin-link" className="flex items-center gap-2 px-3 h-11 sm:h-auto sm:py-2 rounded-md text-[var(--warning)] hover:bg-[rgba(255,200,80,0.08)]">
                      <Shield size={14} /> <span className="text-sm">Admin Panel</span>
                    </NavLink>
                  )}
                  {user && (
                    <button onClick={handleLogout} className="flex items-center gap-2 px-3 h-11 sm:h-auto sm:py-2 rounded-md text-[var(--muted)] hover:text-[var(--bone)] text-sm" data-testid="nav-logout">
                      <LogOut size={14} /> Logout
                    </button>
                  )}
                </div>
              )}
              {!user && (
                <Link to="/login" className="btn mt-3" data-testid="nav-login-button">Sign in</Link>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Active raid alert — visible site-wide whenever a raid boss is
          spawned. Sits just under the header so it's the first thing
          users see when something they can hunt is live. */}
      <RaidBanner />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-10">
        {children}
      </main>

      <footer className="border-t border-white/5 py-6 text-center text-xs text-[var(--dim)] space-y-1.5 px-4">
        <div>
          <ChilletSkull size={12} className="inline mr-1" /> ChillPalServers · Palworld community servers · {new Date().getFullYear()}
        </div>
        <div className="text-[10px] text-[var(--dim)]/80 max-w-2xl mx-auto leading-relaxed">
          Not affiliated with or endorsed by Pocketpair Inc. Palworld and all related assets are trademarks of Pocketpair Inc.
        </div>
      </footer>

      <ChatWidget />

      <PayoutFlash
        show={transferFlash !== null}
        variant="skull"
        amount={transferFlash?.amount || 0}
        label={transferFlash ? `FROM ${(transferFlash.from || "").toUpperCase()}` : "RECEIVED"}
        onDone={() => setTransferFlash(null)}
      />
    </div>
    </>
  );
}
